import React, { useEffect, useState } from "react";
import { ModalProps } from "@components/atoms/Modal/types";
import SlideSheet from "@components/atoms/SlideSheet";
import WebPopup from "@components/atoms/PopUpWeb";
import Toast, { ToastConfig } from "react-native-toast-message";
import { toastConfig } from "@components/molecules/VDBToast";
import { useAppSettings } from "@redux/slices/globalSlice";
import { useTheme } from "styled-components/native";
import { StyleSheet, View } from "react-native";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { getEmbeddedModalMaxHeight } from "@utils/iframeMessaging";

const Modal = ({
  type,
  headerTitle,
  children,
  isOpen,
  onClose,
  paddingLeft,
  paddingRight,
  paddingTop,
  paddingBottom,
  minModalHeight,
  backgroundColor,
  headerBackgroundColor,
  headerTextColor,
  enableToastMessage = false,
  skipKeyboardAware,
  fillContentHeight,
  size,
  style,
  hideHeader,
  transparent,
  noPadding,
  webHeight = "auto",
  showScrollbar = true,
  allowFullScreen,
  isJBT = false,
  disableScrollWeb = false,
  skipKeyboardAwareWeb = false,
  height = "auto",
  clearChat = false,
  handleClearChat = () => {},
  leftIcon,
  leftIconPress,
}: ModalProps) => {
  const theme = useTheme();
  const userInfo = useUserInfo();
  const requestedType = type ? type : theme.isLargeScreen ? "popUp" : "sheet";
  // Inside an iframe a bottom sheet sits at the bottom of the frame, which is
  // often off the visible page. Use the centered popup instead.
  const isSheetAsPopup = requestedType === "sheet" && !!userInfo.isIFrame();
  const modalType = isSheetAsPopup ? "popUp" : requestedType;
  // `vh` matches the iframe, so a tall product page makes the modal just as
  // tall. Cap it to 85% of the device screen instead.
  const embeddedMaxHeight = getEmbeddedModalMaxHeight();
  const requestedHeightFillsFrame =
    typeof height === "string" &&
    (height.endsWith("%") || height.endsWith("vh"));
  const requestedHeightExceedsScreen =
    typeof webHeight === "number" && webHeight > embeddedMaxHeight;
  const popupHeight =
    embeddedMaxHeight &&
    (requestedHeightFillsFrame || requestedHeightExceedsScreen)
      ? `${embeddedMaxHeight}px`
      : webHeight;
  const popupMaxHeight = embeddedMaxHeight
    ? `${embeddedMaxHeight}px`
    : undefined;
  const popupSkipKeyboardAware = isSheetAsPopup
    ? skipKeyboardAwareWeb || !!skipKeyboardAware
    : skipKeyboardAwareWeb;
  const getAppSettings = useAppSettings();
  const [visibilityTime, setVisibilityTime] = useState();
  useEffect(() => {
    if (getAppSettings.toast_display_time) {
      setVisibilityTime(getAppSettings.toast_display_time);
    }
  }, [getAppSettings]);
  return (
    <>
      {modalType === "sheet" ? (
        <SlideSheet
          onClose={onClose as () => void}
          isOpen={isOpen}
          headerTitle={headerTitle}
          paddingLeft={paddingLeft}
          paddingRight={paddingRight}
          paddingTop={paddingTop}
          paddingBottom={paddingBottom}
          minModalHeight={minModalHeight}
          backgroundColor={backgroundColor}
          style={style}
          skipKeyboardAware={skipKeyboardAware}
          fillContentHeight={fillContentHeight}
          allowFullScreen={allowFullScreen}
          height={height}
          clearChat={clearChat}
          handleClearChat={handleClearChat}
          leftIcon={leftIcon}
          leftIconPress={leftIconPress}
        >
          {children}
          {enableToastMessage && (
            <View style={styles.toastLayer} pointerEvents="box-none">
              <Toast
                config={toastConfig as unknown as ToastConfig}
                visibilityTime={visibilityTime ? Number(visibilityTime) : 5000}
              />
            </View>
          )}
        </SlideSheet>
      ) : (
        <WebPopup
          onClose={onClose as () => void}
          isOpen={isOpen}
          title={headerTitle}
          size={size}
          backgroundColor={backgroundColor}
          headerBackgroundColor={headerBackgroundColor}
          headerTextColor={headerTextColor}
          hideHeader={hideHeader}
          transparent={transparent}
          noPadding={noPadding}
          height={popupHeight}
          maxHeight={popupMaxHeight}
          showScrollbar={showScrollbar}
          isJBT={isJBT}
          disableScrollWeb={disableScrollWeb}
          style={style}
          skipKeyboardAwareWeb={popupSkipKeyboardAware}
          leftIcon={leftIcon}
          leftIconPress={leftIconPress}
        >
          {children}
          {enableToastMessage && (
            <View style={styles.toastLayer} pointerEvents="box-none">
              <Toast
                config={toastConfig as unknown as ToastConfig}
                visibilityTime={visibilityTime ? Number(visibilityTime) : 5000}
              />
            </View>
          )}
        </WebPopup>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  toastLayer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    elevation: 9999,
  },
});

export default Modal;
